import React, { useState, useEffect } from 'react';
import { Grid, TextField, Typography, Box } from '@mui/material';

const CustomDateInput = ({ 
  label, 
  value, 
  onChange, 
  required = false, 
  disabled = false,
  error = false,
  helperText = ''
}) => {
  const [day, setDay] = useState('');
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');
  const [localError, setLocalError] = useState('');

  // Parse initial value if it exists
  useEffect(() => {
    if (value) {
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          setDay(date.getDate().toString());
          setMonth((date.getMonth() + 1).toString());
          setYear(date.getFullYear().toString());
        }
      } catch (e) {
        // If parsing fails, leave inputs empty
        setDay('');
        setMonth('');
        setYear('');
      }
    } else {
      setDay('');
      setMonth('');
      setYear('');
    }
  }, [value]);

  // Validate and format date
  const validateAndFormatDate = (d, m, y) => {
    // Clear previous errors
    setLocalError('');

    // Check if all fields are filled
    if (!d || !m || !y) {
      if (required) {
        setLocalError('Todos los campos son obligatorios');
        return null;
      }
      return null;
    }

    // Convert to numbers
    const dayNum = parseInt(d, 10);
    const monthNum = parseInt(m, 10);
    const yearNum = parseInt(y, 10);

    // Validate ranges
    if (yearNum < 1900 || yearNum > 2100) {
      setLocalError('El año debe estar entre 1900 y 2100');
      return null;
    }

    if (monthNum < 1 || monthNum > 12) {
      setLocalError('El mes debe estar entre 1 y 12');
      return null;
    }

    // Get days in month
    const daysInMonth = new Date(yearNum, monthNum, 0).getDate();
    if (dayNum < 1 || dayNum > daysInMonth) {
      setLocalError(`El día debe estar entre 1 y ${daysInMonth} para el mes ${monthNum}`);
      return null;
    }

    // Create date and validate it's not in the future
    const date = new Date(yearNum, monthNum - 1, dayNum);
    const today = new Date();
    today.setHours(23, 59, 59, 999); // End of today

    if (date > today) {
      setLocalError('La fecha no puede ser en el futuro');
      return null;
    }

    // Return ISO string format
    return date.toISOString();
  };

  // Handle individual field changes
  const handleDayChange = (e) => {
    const newDay = e.target.value;
    setDay(newDay);
    
    if (newDay && month && year) {
      const formattedDate = validateAndFormatDate(newDay, month, year);
      if (formattedDate) {
        onChange(formattedDate);
      }
    } else if (!newDay && !month && !year) {
      onChange(''); // Clear the date if all fields are empty
    }
  };

  const handleMonthChange = (e) => {
    const newMonth = e.target.value;
    setMonth(newMonth);
    
    if (day && newMonth && year) {
      const formattedDate = validateAndFormatDate(day, newMonth, year);
      if (formattedDate) {
        onChange(formattedDate);
      }
    } else if (!day && !newMonth && !year) {
      onChange(''); // Clear the date if all fields are empty
    }
  };

  const handleYearChange = (e) => {
    const newYear = e.target.value;
    setYear(newYear);
    
    if (day && month && newYear) {
      const formattedDate = validateAndFormatDate(day, month, newYear);
      if (formattedDate) {
        onChange(formattedDate);
      }
    } else if (!day && !month && !newYear) {
      onChange(''); // Clear the date if all fields are empty
    }
  };

  const hasError = Boolean(error || localError);
  const displayHelperText = localError || helperText;

  return (
    <Box>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        {label} {required && '*'}
      </Typography>
      <Grid container spacing={1}>
        <Grid item xs={4}>
          <TextField
            fullWidth
            label="Día"
            type="number"
            value={day}
            onChange={handleDayChange}
            disabled={disabled}
            error={hasError}
            inputProps={{ 
              min: 1, 
              max: 31,
              style: { textAlign: 'center' }
            }}
            size="small"
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            fullWidth
            label="Mes"
            type="number"
            value={month}
            onChange={handleMonthChange}
            disabled={disabled}
            error={hasError}
            inputProps={{ 
              min: 1, 
              max: 12,
              style: { textAlign: 'center' }
            }}
            size="small"
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            fullWidth
            label="Año"
            type="number"
            value={year}
            onChange={handleYearChange}
            disabled={disabled}
            error={hasError}
            inputProps={{ 
              min: 1900, 
              max: 2100,
              style: { textAlign: 'center' }
            }}
            size="small"
          />
        </Grid>
      </Grid>
      {displayHelperText && (
        <Typography 
          variant="caption" 
          color={hasError ? "error" : "textSecondary"}
          sx={{ mt: 0.5, display: 'block' }}
        >
          {displayHelperText}
        </Typography>
      )}
    </Box>
  );
};

export default CustomDateInput;
